import { useEffect, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Badge, Button, Card, Spinner, Alert, AlertDescription,
  Table, TableHeader, TableBody, TableHead, TableRow, TableCell,
  SearchInput, EmptyState,
} from '@agion/ui'
import { Globe, Plus } from 'lucide-react'
import { PageHeader } from '@/layouts/InfraPageHeader'
import { listDnsRecords } from './infra_dns.service'

interface DNSRecord {
  id: string
  domain: string
  type: 'A' | 'AAAA' | 'CNAME' | 'MX' | 'TXT' | 'NS' | 'SRV'
  value: string
  ttl: number
  status: 'active' | 'pending' | 'error'
}

const typeVariant: Record<DNSRecord['type'], 'default' | 'info' | 'success' | 'warning'> = {
  A: 'info',
  AAAA: 'info',
  CNAME: 'success',
  MX: 'warning',
  TXT: 'default',
  NS: 'default',
  SRV: 'warning',
}

const statusVariant: Record<DNSRecord['status'], 'success' | 'info' | 'error'> = {
  active: 'success',
  pending: 'info',
  error: 'error',
}

export function DNSPage() {
  const { t } = useTranslation(['pages'])
  const [records, setRecords] = useState<DNSRecord[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')

  const loadRecords = useCallback(async () => {
    try {
      setIsLoading(true)
      setError(null)
      const response = await listDnsRecords()
      const mapped: DNSRecord[] = response.data.map(r => ({
        id: r.id,
        domain: r.name,
        type: r.type as DNSRecord['type'],
        value: r.value,
        ttl: r.ttl,
        status: 'active' as const,
      }))
      setRecords(mapped)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load DNS records')
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadRecords()
  }, [loadRecords])

  const filtered = records.filter(rec =>
    rec.domain.toLowerCase().includes(searchQuery.toLowerCase()) ||
    rec.value.toLowerCase().includes(searchQuery.toLowerCase()) ||
    rec.type.toLowerCase().includes(searchQuery.toLowerCase())
  )

  return (
    <div className="p-6 space-y-6">
      <PageHeader
        title={t('pages:dns.title', 'DNS Records')}
        description={t('pages:dns.description', 'Manage DNS records for your domains')}
        icon={Globe}
        actions={
          <Button variant="primary" size="sm">
            <Plus size={14} className="mr-1" />
            {t('pages:dns.addRecord', 'Add Record')}
          </Button>
        }
      />

      <div className="max-w-md">
        <SearchInput
          placeholder={t('pages:dns.search', 'Search by domain, type, or value...')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onClear={() => setSearchQuery('')}
        />
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Spinner />
        </div>
      ) : error ? (
        <Alert variant="error">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : filtered.length === 0 ? (
        <Card>
          <EmptyState
            icon={Globe}
            title={t('pages:dns.empty.title', 'No DNS records')}
            description={
              searchQuery
                ? t('pages:dns.empty.searchDescription', 'No records match your search criteria.')
                : t('pages:dns.empty.description', 'Add your first DNS record to get started.')
            }
            action={
              !searchQuery ? (
                <Button variant="primary" size="sm">
                  <Plus size={14} className="mr-1" />
                  {t('pages:dns.addRecord', 'Add Record')}
                </Button>
              ) : undefined
            }
          />
        </Card>
      ) : (
        <Table variant="modern">
          <TableHeader variant="modern">
            <TableRow variant="modern">
              <TableHead variant="modern">{t('pages:dns.columns.domain', 'Domain')}</TableHead>
              <TableHead variant="modern">{t('pages:dns.columns.type', 'Type')}</TableHead>
              <TableHead variant="modern">{t('pages:dns.columns.value', 'Value')}</TableHead>
              <TableHead variant="modern">{t('pages:dns.columns.ttl', 'TTL')}</TableHead>
              <TableHead variant="modern">{t('pages:dns.columns.status', 'Status')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody variant="modern">
            {filtered.map(rec => (
              <TableRow key={rec.id} variant="modern">
                <TableCell className="font-medium">{rec.domain}</TableCell>
                <TableCell>
                  <Badge variant={typeVariant[rec.type]}>{rec.type}</Badge>
                </TableCell>
                <TableCell className="font-mono text-xs text-slate-600 dark:text-slate-400 max-w-xs truncate">
                  {rec.value}
                </TableCell>
                <TableCell className="text-slate-500">{rec.ttl}s</TableCell>
                <TableCell>
                  <Badge variant={statusVariant[rec.status]}>{rec.status}</Badge>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  )
}
